import React, { useEffect, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components/macro'
import {
  WallIncident,
  Organization,
  WallHistoryRequest,
  HistoryType,
  ChatActionType,
  IncidentEscortState,
  IncidentLocation
} from 'incident-code-core'
import { AxiosError } from 'axios'

import { colors, fonts, images, strings } from '../../../../constants'
import { orgService } from '../../../../services/orgs-service'
import { historyService } from '../../../../services/history-service'
import { Status } from './Status'
import Intrado from '../../../../components/Intrado/Intrado'
import { chatService } from '../../../../services/chat-service'
import { EscortCardStateLabel } from '../../../../scenes/AllIncidents/Cards/EscortCard/Label'

interface subHeaderIncident extends WallIncident {
  publicCode?: string
}

interface ISubHeaderProps extends RouteComponentProps {
  incident: subHeaderIncident
  status: IncidentEscortState
  showNotificationForm(): void
  locationData?: IncidentLocation
}

const SubHeader: React.FC<ISubHeaderProps> = props => {
  const [organization, setOrganization] = useState<Organization>({})
  const { incident, locationData, status } = props

  useEffect(() => {
    let isMounted = true

    if (incident.organization) {
      orgService
        .getOrganizationInfoById(incident.organization.toString())
        .then(response => {
          if (isMounted) {
            setOrganization(response.data)
          }
        })
        .catch((error: AxiosError) => console.error(error.message))
    }

    return () => {
      isMounted = false
    }
  }, [incident.organization])

  const onCloseDetail = () => {
    const { id, isResolved } = incident
    if (isResolved) {
      window.close()
    }

    const payload: WallHistoryRequest = {
      incidentId: `${id}`,
      type: HistoryType.Chat,
      action: ChatActionType.Closed
    }
    chatService.removeParticipants(incident)
    historyService.create(payload).then(() => window.close())
  }

  const showNotificationForm = () => props.showNotificationForm()

  return (
    <Container>
      <Back>
        <CloseButton onClick={onCloseDetail} />
      </Back>
      <Incident>
        <Title>
          <TitleText>
            #{incident.id.toString().slice(-5)}

            {(() => {
              const panicMode = (incident as any).panicMode;

              const isPanicIncident =
                panicMode !== undefined
                  ? panicMode !== "none"
                  : incident.isPanic === true;

              const isDirectPanic = panicMode === "direct";
              const isEscalatedPanic = panicMode === "escalated";

              // 1️⃣ Direct panic, not yet marked safe
              if (isDirectPanic && !incident.isSafe) {
                return " Duress Activated";
              }

              // 2️⃣ Direct panic + user pressed Safe
              if (isDirectPanic && incident.isSafe) {
                return " Duress";
              }

              // 3️⃣ Virtual escort (normal OR escalated panic)
              // NOTE: even if panic is escalated during virtual escort,
              // UI should still show "Active - Virtual Escort"
              return " Active - Virtual Escort";
            })()}

            {organization?.name ? ` - ${organization.name}` : " "}
            {!!incident.publicCode && ` - ${incident.publicCode}`}
          </TitleText>
        </Title>
      </Incident>

      <Status 
        status={props.status}
        type={incident.type}
        timerExpired={incident.escort?.fromTimerExpired}
        isPanic={incident.isPanic} isSafe={incident.isSafe}
        panicMode={(incident as any).panicMode} 
      />
      
      {(locationData || (incident && incident.location)) && (
        <IntradoWrap>
          <Intrado locationData={locationData} incident={incident} />
        </IntradoWrap>
      )}
      <NotifyUsers>
        <NotificationButton onClick={showNotificationForm}>
          {strings.incidentDetail.buttons.notifyUsers}
        </NotificationButton>
      </NotifyUsers>
    </Container>
  )
}

export const SubHeaderWithRouter = withRouter(SubHeader)

const Container = styled.div`
  padding: 0 20px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  background-color: ${colors.black};
`

const Title = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const TitleText = styled.h2`
  font-family: ${fonts.defaultFont};
  font-size: 18px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 20px;
  letter-spacing: -0.2px;
  text-align: center;
  color: ${colors.ghostWhite};
`

const Back = styled.div`
  flex-grow: 1;
  align-items: flex-start;
`

const Incident = styled.div`
  flex-grow: 4;
  align-items: flex-start;
`

const NotifyUsers = styled.div`
  flex-grow: 1;
`

const CloseButton = styled.button`
  display: flex;
  align-items: flex-start;
  background-color: inherit;
  background: url(${images.close}) right no-repeat;
  width: 30px;
  border: none;
  outline: none;
  color: ${colors.midnight};
  font-size: 16px;
  height: 32px;
  &:hover {
    cursor: pointer;
  }
`

const NotificationButton = styled.button`
  display: flex;
  align-items: flex-end;
  background: linear-gradient(279deg, #503bff, #a11de7);
  color: ${colors.ghostWhite};
  border: none;
  font-size: 16px;
  font-family: ${fonts.defaultFont};
  padding: 10px;
`

const IntradoWrap = styled.div`
  flex-grow: 1;
`
